import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  LinearProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  RadialBarChart,
  RadialBar,
  Legend
} from 'recharts';
import { 
  Target, 
  TrendingUp, 
  TrendingDown, 
  Activity, 
  Award, 
  Calendar,
  Flame,
  CheckCircle,
  AlertTriangle,
  Star
} from 'lucide-react';
import { DatabaseSchema } from '../types';
import { format, subDays, differenceInDays, startOfWeek, endOfWeek } from 'date-fns';

interface PerformanceDashboardProps {
  database: DatabaseSchema | null;
}

export const PerformanceDashboard: React.FC<PerformanceDashboardProps> = ({ database }) => {
  if (!database) {
    return (
      <Box p={3}>
        <Typography>Loading performance data...</Typography>
      </Box>
    );
  }

  const { weights, goals, exercises, supplements, meals, bodyMeasurements } = database;

  // Performance Metrics Calculations
  const getPerformanceMetrics = () => {
    const today = new Date();
    const weekStart = startOfWeek(today);
    const weekEnd = endOfWeek(today);
    
    // Weight Loss Rate
    const recentWeights = weights
      .filter(w => new Date(w.date) >= subDays(today, 30))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    
    const weightLossRate = recentWeights.length >= 2
      ? (() => {
          const daysBetween = differenceInDays(new Date(recentWeights[recentWeights.length - 1].date), new Date(recentWeights[0].date));
          return daysBetween > 0
            ? (recentWeights[0].weight - recentWeights[recentWeights.length - 1].weight) / daysBetween * 7
            : 0;
        })()
      : 0;

    // Consistency Scores
    const last7Days = Array.from({ length: 7 }, (_, i) => format(subDays(today, i), 'yyyy-MM-dd'));
    
    const exerciseConsistency = last7Days.filter(date => 
      exercises.some(e => e.date === date && e.completed)
    ).length / 7 * 100;

    const supplementConsistency = last7Days.filter(date => {
      const entry = supplements.find(s => s.date === date);
      if (!entry) return false;
      const totalSupplements = Object.keys(entry.supplements).length;
      const takenSupplements = Object.values(entry.supplements).filter(Boolean).length;
      return takenSupplements >= Math.ceil(totalSupplements * 0.6); // At least 60% adherence
    }).length / 7 * 100;

    const mealConsistency = last7Days.filter(date => {
      const dayMeals = meals.filter(m => m.date === date);
      return dayMeals.length > 0 && dayMeals.filter(m => m.completed).length / dayMeals.length >= 0.8;
    }).length / 7 * 100;

    // Goal Progress
    const sortedAllWeights = [...weights].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    const latestWeight = sortedAllWeights.length > 0 ? sortedAllWeights[sortedAllWeights.length - 1].weight : 0;
    const goalDenominator = goals ? goals.initialWeight - goals.targetWeight : 0;
    const goalProgress = goals && goalDenominator !== 0 ?
      ((goals.initialWeight - latestWeight) / goalDenominator) * 100 : 0;

    // Activity Streaks
    const exerciseStreak = calculateStreak(exercises.map(e => ({ date: e.date, completed: e.completed })));
    const supplementStreak = calculateStreak(supplements.map(s => {
      const totalSupplements = Object.keys(s.supplements).length;
      const takenSupplements = Object.values(s.supplements).filter(Boolean).length;
      return { 
        date: s.date, 
        completed: takenSupplements >= Math.ceil(totalSupplements * 0.6)
      };
    }));

    return {
      weightLossRate: weightLossRate.toFixed(2),
      exerciseConsistency: exerciseConsistency.toFixed(1),
      supplementConsistency: supplementConsistency.toFixed(1),
      mealConsistency: mealConsistency.toFixed(1),
      goalProgress: Math.max(0, goalProgress).toFixed(1),
      exerciseStreak,
      supplementStreak,
      overallScore: ((exerciseConsistency + supplementConsistency + mealConsistency) / 3).toFixed(1)
    };
  };

  const calculateStreak = (items: { date: string; completed: boolean }[]) => {
    const completedItems = items
      .filter(item => item.completed)
      .map(item => item.date)
      .sort();
    
    if (completedItems.length === 0) return 0;
    
    let streak = 1;
    const today = format(new Date(), 'yyyy-MM-dd');
    
    // Check if streak includes today or yesterday
    const lastEntry = completedItems[completedItems.length - 1];
    const daysSinceLastEntry = differenceInDays(new Date(today), new Date(lastEntry));
    
    if (daysSinceLastEntry > 1) return 0; // Streak broken
    
    // Count backwards from the most recent entry
    for (let i = completedItems.length - 2; i >= 0; i--) {
      const currentDate = new Date(completedItems[i + 1]);
      const prevDate = new Date(completedItems[i]);
      
      if (differenceInDays(currentDate, prevDate) === 1) {
        streak++;
      } else {
        break;
      }
    }
    
    return streak;
  };

  // Weekly Performance Data
  const getWeeklyPerformance = () => {
    const last8Weeks = Array.from({ length: 8 }, (_, i) => {
      const weekStart = startOfWeek(subDays(new Date(), i * 7));
      const weekEnd = endOfWeek(weekStart);
      
      const weekExercises = exercises.filter(e => {
        const date = new Date(e.date);
        return date >= weekStart && date <= weekEnd;
      });
      
      const weekSupplements = supplements.filter(s => {
        const date = new Date(s.date);
        return date >= weekStart && date <= weekEnd;
      });

      const weekMeals = meals.filter(m => {
        const date = new Date(m.date);
        return date >= weekStart && date <= weekEnd;
      });

      return {
        week: format(weekStart, 'MMM dd'),
        exercises: weekExercises.reduce((sum, e) => sum + e.duration, 0),
        exercisesSessions: weekExercises.filter(e => e.completed).length,
        supplementsScore: weekSupplements.length > 0 ?
          weekSupplements.reduce((sum, s) => {
            const total = Object.keys(s.supplements).length;
            const taken = Object.values(s.supplements).filter(Boolean).length;
            return sum + (total > 0 ? (taken / total) * 100 : 0);
          }, 0) / weekSupplements.length : 0,
        mealsScore: weekMeals.length > 0 ? 
          weekMeals.filter(m => m.completed).length / weekMeals.length * 100 : 0
      };
    }).reverse();
    
    return last8Weeks;
  };

  // Achievement System
  const getAchievements = () => {
    const achievements = [];
    const metrics = getPerformanceMetrics();
    
    if (metrics.exerciseStreak >= 7) {
      achievements.push({ 
        title: 'Exercise Champion', 
        description: `${metrics.exerciseStreak} day exercise streak!`,
        icon: Award,
        color: '#22c55e'
      });
    }

    if (metrics.supplementStreak >= 14) {
      achievements.push({
        title: 'Consistency Master',
        description: `${metrics.supplementStreak} day supplement streak!`,
        icon: Star,
        color: '#f59e0b'
      });
    }

    if (parseFloat(metrics.overallScore) >= 80) {
      achievements.push({
        title: 'High Performer',
        description: `${metrics.overallScore}% overall consistency!`,
        icon: Target,
        color: '#6366f1'
      });
    }

    if (weights.length >= 10) {
      achievements.push({
        title: 'Dedicated Tracker',
        description: `${weights.length} weight entries logged!`,
        icon: TrendingDown,
        color: '#8b5cf6'
      });
    }
    
    return achievements;
  };

  const metrics = getPerformanceMetrics();
  const weeklyData = getWeeklyPerformance();
  const achievements = getAchievements();

  const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444'];

  const consistencyData = [
    { name: 'Exercise', value: parseFloat(metrics.exerciseConsistency), color: '#6366f1' },
    { name: 'Supplements', value: parseFloat(metrics.supplementConsistency), color: '#10b981' },
    { name: 'Meals', value: parseFloat(metrics.mealConsistency), color: '#f59e0b' },
  ];

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Performance Dashboard
      </Typography>

      {/* Performance Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <TrendingDown size={24} color="#6366f1" />
              <Typography variant="h4" color="primary" fontWeight="bold">
                {metrics.weightLossRate}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                kg/week rate
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Target size={24} color="#10b981" />
              <Typography variant="h4" color="success.main" fontWeight="bold">
                {metrics.overallScore}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Overall Score
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Flame size={24} color="#f59e0b" />
              <Typography variant="h4" color="warning.main" fontWeight="bold">
                {metrics.exerciseStreak}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Exercise Streak
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Activity size={24} color="#8b5cf6" />
              <Typography variant="h4" color="secondary.main" fontWeight="bold">
                {metrics.goalProgress}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Goal Progress
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Consistency Scores */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                7-Day Consistency
              </Typography>
              
              {consistencyData.map((item) => (
                <Box key={item.name} mb={2}>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">{item.name}</Typography>
                    <Typography variant="body2">{item.value.toFixed(1)}%</Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={item.value}
                    sx={{ 
                      height: 8, 
                      borderRadius: 4,
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: item.color
                      }
                    }}
                  />
                </Box>
              ))}

              <Box mt={3}>
                <Typography variant="subtitle2" gutterBottom>
                  Performance Insights
                </Typography>
                {parseFloat(metrics.overallScore) >= 80 ? (
                  <Alert severity="success" icon={<CheckCircle />}>
                    Excellent consistency! Keep up the great work.
                  </Alert>
                ) : parseFloat(metrics.overallScore) >= 60 ? (
                  <Alert severity="info" icon={<TrendingUp />}>
                    Good progress. Focus on your lowest scoring area.
                  </Alert>
                ) : (
                  <Alert severity="warning" icon={<AlertTriangle />}>
                    Room for improvement. Try to be more consistent.
                  </Alert>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Weekly Performance Trend */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Weekly Performance Trend
              </Typography>
              <Box height={300}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={weeklyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="week" 
                      stroke="#64748b"
                      fontSize={12}
                    />
                    <YAxis 
                      stroke="#64748b"
                      fontSize={12}
                    />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                        color: '#0f172a'
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="exercises" 
                      stroke="#6366f1" 
                      strokeWidth={2}
                      dot={{ fill: '#6366f1', strokeWidth: 2, r: 3 }}
                      activeDot={{ r: 5, stroke: '#6366f1', strokeWidth: 2 }}
                      name="Exercise Minutes"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="supplementsScore" 
                      stroke="#10b981" 
                      strokeWidth={2}
                      dot={{ fill: '#10b981', strokeWidth: 2, r: 3 }}
                      activeDot={{ r: 5, stroke: '#10b981', strokeWidth: 2 }}
                      name="Supplements %"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="mealsScore" 
                      stroke="#f59e0b" 
                      strokeWidth={2}
                      dot={{ fill: '#f59e0b', strokeWidth: 2, r: 3 }}
                      activeDot={{ r: 5, stroke: '#f59e0b', strokeWidth: 2 }}
                      name="Meals %"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Consistency Pie Chart */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Consistency Breakdown
              </Typography>
              <Box height={300}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={consistencyData}
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      innerRadius={40}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value.toFixed(1)}%`}
                      labelLine={false}
                    >
                      {consistencyData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                        color: '#0f172a'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Achievements */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Achievements
              </Typography>
              
              {achievements.length > 0 ? (
                <List>
                  {achievements.map((achievement, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <achievement.icon size={24} color={achievement.color} />
                      </ListItemIcon>
                      <ListItemText
                        primary={achievement.title}
                        secondary={achievement.description}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography color="text.secondary">
                  Keep working towards your goals to unlock achievements!
                </Typography>
              )}

              <Box mt={2}>
                <Typography variant="subtitle2" gutterBottom>
                  Next Milestones
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  <Chip size="small" label="7-day exercise streak" />
                  <Chip size="small" label="80% consistency score" />
                  <Chip size="small" label="10 weight entries" />
                  <Chip size="small" label="14-day supplement streak" />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Weekly Activity Summary */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Weekly Activity Summary
              </Typography>
              <Box height={250}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={weeklyData} barGap={4}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="week" 
                      stroke="#64748b"
                      fontSize={12}
                    />
                    <YAxis 
                      stroke="#64748b"
                      fontSize={12}
                    />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                        color: '#0f172a'
                      }}
                    />
                    <Bar 
                      dataKey="exercises" 
                      fill="#6366f1" 
                      name="Exercise Minutes"
                      radius={[2, 2, 0, 0]}
                    />
                    <Bar 
                      dataKey="exercisesSessions" 
                      fill="#10b981" 
                      name="Exercise Sessions"
                      radius={[2, 2, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};